import { useEffect, useRef, useState } from "react";
import { RiCloseFill, RiQuillPenLine } from "react-icons/ri";
import { colors } from "@styles/colors";

interface SignatureModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (dataUrl: string) => void;
}

/**
 * 全螢幕「橫向」手寫簽名板。
 * 手機多為直向，簽名寬度不足，故將整個簽名板旋轉 90° 呈現為橫向（使用者將手機轉為橫向書寫）。
 * canvas 需要指令式操作，且因外層 rotate(90deg)，指標座標需做「逆旋轉」換算回 canvas 本地座標，
 * 否則畫出來會偏移。輸出 PNG dataURL 給契約頁顯示。
 */
const SignatureModal = ({ open, onClose, onConfirm }: SignatureModalProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const [hasInk, setHasInk] = useState(false);

  useEffect(() => {
    if (!open) return;
    setHasInk(false);
    const t = setTimeout(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const dpr = window.devicePixelRatio || 1;
      // 用 layout 尺寸（offsetWidth/Height，不受 transform 影響）設定畫布背景緩衝區。
      const cw = canvas.offsetWidth;
      const ch = canvas.offsetHeight;
      canvas.width = cw * dpr;
      canvas.height = ch * dpr;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#1E3A8A";
      ctx.lineWidth = 3.5;
    }, 80);
    return () => clearTimeout(t);
  }, [open]);

  // 外層 rotate(90deg)：把 viewport 指標座標逆旋轉回 canvas 本地座標。
  //  rotate(90°) 下 local(lx,ly) → screenΔ(dx,dy) = (-ly, lx)，逆解得 lx=dy, ly=-dx。
  const getPos = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const touch = "touches" in e ? e.touches[0] : (e as React.MouseEvent);
    const dx = touch.clientX - cx;
    const dy = touch.clientY - cy;
    return { x: canvas.offsetWidth / 2 + dy, y: canvas.offsetHeight / 2 - dx };
  };

  const start = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    drawing.current = true;
    last.current = getPos(e);
  };
  const move = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current?.getContext("2d");
    if (!ctx) return;
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasInk) setHasInk(true);
  };
  const end = () => {
    drawing.current = false;
  };
  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
  };
  const confirm = () => {
    if (!hasInk || !canvasRef.current) return;
    onConfirm(canvasRef.current.toDataURL("image/png"));
  };

  if (!open) return null;

  return (
    <div style={{ position: "absolute", inset: 0, zIndex: 200, background: "#fff", overflow: "hidden" }}>
      {/* 橫向容器：尺寸交換（寬 100vh、高 100vw）後 rotate 90° 剛好鋪滿直向 viewport */}
      <div
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "100vh",
          height: "100vw",
          transform: "translate(-50%, -50%) rotate(90deg)",
          transformOrigin: "center",
          background: "#fff",
          display: "flex",
          flexDirection: "column",
        }}>
        {/* 標題列 */}
        <div
          style={{
            padding: "14px 20px",
            display: "flex",
            alignItems: "center",
            gap: 14,
            borderBottom: `1px solid ${colors.borderLight}`,
            flexShrink: 0,
          }}>
          <button
            onClick={onClose}
            style={{
              width: 32,
              height: 32,
              border: "none",
              background: "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              padding: 0,
            }}>
            <RiCloseFill size={22} color={colors.grayDark} />
          </button>
          <div style={{ fontSize: 18, fontWeight: 800, color: colors.grayDark }}>親筆簽名</div>
          <div style={{ marginLeft: "auto", fontSize: 12, color: colors.hint }}>請橫向書寫</div>
        </div>

        {/* 簽名區 */}
        <div style={{ flex: 1, position: "relative", background: "#FFFDF7", overflow: "hidden" }}>
          <div
            style={{
              position: "absolute",
              left: 40,
              right: 40,
              bottom: "26%",
              borderTop: "1.5px dashed #FBBF24",
              opacity: hasInk ? 0 : 0.7,
              pointerEvents: "none",
              transition: "opacity 0.2s",
            }}
          />
          {!hasInk && (
            <div
              style={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                gap: 10,
              }}>
              <RiQuillPenLine size={30} color={colors.amber} />
              <div style={{ fontSize: 16, color: colors.hint, fontWeight: 500, letterSpacing: 1 }}>請在此處簽名</div>
              <div style={{ fontSize: 12, color: colors.gray200 }}>可使用手指或觸控筆,簽好按「完成簽名」</div>
            </div>
          )}
          <canvas
            ref={canvasRef}
            style={{ width: "100%", height: "100%", touchAction: "none", cursor: "crosshair", display: "block" }}
            onMouseDown={start}
            onMouseMove={move}
            onMouseUp={end}
            onMouseLeave={end}
            onTouchStart={start}
            onTouchMove={move}
            onTouchEnd={end}
          />
        </div>

        {/* 底部按鈕 */}
        <div
          style={{
            padding: "12px 20px",
            borderTop: `1px solid ${colors.borderLight}`,
            display: "flex",
            gap: 12,
            flexShrink: 0,
          }}>
          <button
            onClick={clear}
            disabled={!hasInk}
            style={{
              flex: 1,
              padding: "14px",
              borderRadius: 12,
              border: `1.5px solid ${colors.border}`,
              background: "#fff",
              color: hasInk ? colors.danger : colors.gray200,
              fontSize: 15,
              fontWeight: 600,
              cursor: hasInk ? "pointer" : "default",
            }}>
            清除重簽
          </button>
          <button
            onClick={confirm}
            disabled={!hasInk}
            className={hasInk ? "primary-bg-gradient" : undefined}
            style={{
              flex: 1.6,
              padding: "14px",
              borderRadius: 12,
              border: "none",
              background: hasInk ? undefined : colors.bgGray1,
              color: hasInk ? "#fff" : colors.gray200,
              fontSize: 15,
              fontWeight: 700,
              cursor: hasInk ? "pointer" : "default",
            }}>
            完成簽名
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;
